import { useCallback } from 'react';
import { Button, Card, Form, Row, Space } from 'antd';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';

import { useResource } from 'app/common/api';
import { AppPage } from 'app/common/app-page/features/app-page';
import {
  NotificationsRender,
  useNotifyFactory,
} from 'app/common/notifications/features/notifications';
import { useAuthStatusWriter } from 'app/railways/domains/auth/features/auth-status';
import { TPersonData } from 'app/railways/domains/passengers/core/person';
import { addPersonRequest } from 'app/railways/domains/passengers/requests';
import { appRoute } from 'app/railways/router/routes';

import { Loading } from '../../../../../ui';
import { PageContent } from '../../../../../ui/page-content';
import { CredentialFields } from './credential-fields';
import { StatusDataFields } from './status-data-fields';

type NewPerson = Omit<TPersonData, 'id'>;

type FormFields = NewPerson;

export const AddPersonPage = observer(() => {
  const navigate = useNavigate();
  const authStatusStore = useAuthStatusWriter();
  const notify = useNotifyFactory();

  const addPersonResource = useResource<NewPerson, TPersonData>({
    request: addPersonRequest,
    errorNotify: notify,
    onAuthRequired: () => authStatusStore.denyAccess(),
    onDone(result) {
      const path = appRoute.PERSON.DETAILS.buildPath({
        passengerId: result.id,
      });
      navigate(path);
    },
  });

  const isLoading = addPersonResource.isLoading;

  const handleFormSubmit = useCallback(
    (person: FormFields) => {
      addPersonResource.send(person);
    },
    [addPersonResource],
  );

  return (
    <AppPage perm="PERM_PERSON_CREATE">
      <PageContent>
        <Form
          layout="vertical"
          onFinish={handleFormSubmit}
          scrollToFirstError={{ behavior: 'smooth' }}
        >
          <Loading active={isLoading}>
            <Space
              direction="vertical"
              size="middle"
              style={{ display: 'flex' }}
            >
              <Card size="small" title="Данные аккаунта">
                <CredentialFields />
              </Card>
              <Card size="small" title="Статус данных">
                <StatusDataFields />
              </Card>

              <NotificationsRender notifyStore={notify} />

              <Card size="small">
                <Row>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ marginLeft: 'auto' }}
                  >
                    Добавить пассажира
                  </Button>
                </Row>
              </Card>
            </Space>
          </Loading>
        </Form>
      </PageContent>
    </AppPage>
  );
});
